import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// @mui
import { Box, Skeleton } from '@mui/material';

// ----------------------------------------------------------------------
// A section of the record that waits until it is nearly on screen before it
// mounts — and therefore before it asks the server anything.
//
// The whole record is one page, as it should be: nothing is hidden behind a
// click. But a client record has nine sections and eleven queries behind
// them, and most clients have nothing in most of them. Loading all eleven
// before the reader has scrolled past the company's address made the page
// slow for everyone and useful to no one.
//
// So the page is whole and the loading is not: each section comes in as the
// reader reaches it, 400px ahead so it is ready by the time it arrives, and
// once mounted it stays mounted.

LazySection.propTypes = {
  children: PropTypes.node,
  // Mount at once, whatever the scroll position — used when something else
  // on the page needs this section alive (the dialer, say).
  force: PropTypes.bool,
  // Roughly how tall the section will be, so the scrollbar does not jump.
  minHeight: PropTypes.number,
  id: PropTypes.string
};

export default function LazySection({
  children,
  force = false,
  minHeight = 320,
  id
}) {
  const [shown, setShown] = useState(force);
  const box = useRef(null);

  useEffect(() => {
    if (force) setShown(true);
  }, [force]);

  useEffect(() => {
    if (shown) return undefined;
    const element = box.current;
    if (!element) return undefined;
    // No observer (old browser, or a test renderer): show it rather than
    // leave the reader with a permanent grey block.
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [shown]);

  return (
    <Box ref={box} id={id} sx={{ scrollMarginTop: 96 }}>
      {shown ? (
        children
      ) : (
        <Skeleton
          variant="rectangular"
          height={minHeight}
          sx={{ borderRadius: 2 }}
        />
      )}
    </Box>
  );
}
